import type { LyricLine } from './lyricsTransform';

/**
 * Diamond (vajra) layout grouping, ported from jnana-native's lyricsView.tsx.
 *
 * In the diamond layout a stanza's trailing verse number is lifted out of the
 * lyric text and shown once, in a narrow gutter to the right of the whole stanza.
 * This module owns the two pieces of that: the tail regex and the grouping pass.
 */

/**
 * Trailing verse number, e.g. `abhayavanu koḍu tāye    ...1` — two or more ASCII
 * dots followed by digits, anchored at the end of the line. Character-identical to
 * native's `endPattern` in lyricsView.tsx.
 *
 * This is deliberately NOT lyricsTransform's LINE_NUMBER_TAIL, which matches the
 * single U+2026 ellipsis character. Real lyrics use ASCII dots, so those tails
 * currently survive into the rendered text; native only strips them inside the
 * diamond layout, so widening LINE_NUMBER_TAIL would silently change normal-mode
 * rendering for every bhajan in the catalog.
 */
const DIAMOND_VERSE_TAIL = /\s*\.{2,}(\d+)\s*$/;

export interface VerseNumberSplit {
  number: number | null;
  cleanedText: string;
}

/** Split a trailing `...N` verse number off a lyric line. */
export function extractVerseNumber(text: string): VerseNumberSplit {
  const match = text.match(DIAMOND_VERSE_TAIL);
  if (match) {
    return {
      number: parseInt(match[1], 10),
      cleanedText: text.replace(DIAMOND_VERSE_TAIL, '').trim(),
    };
  }
  return { number: null, cleanedText: text };
}

export type DiamondChunk =
  /** A run of lyric/svara lines rendered as one block, with an optional gutter number. */
  | { kind: 'stanza'; key: number; lines: LyricLine[]; verseNumber: number | null }
  /** A blank spacer or a section heading — rendered as a sibling, never inside a stanza. */
  | { kind: 'line'; key: number; line: LyricLine };

/**
 * Segment a flat line stream into diamond stanzas.
 *
 * A native stanza is a BLANK-LINE delimited run (parser.ts commits a stanza only on
 * a blank line and at end of input) — not a heading-delimited group, which is what
 * lyricsTransform's `groupStanzas` produces. Section headings additionally break the
 * run here because native renders the heading as a sibling ABOVE the stanza row; a
 * heading inside the row would land in the verse-number gutter.
 *
 * Verse numbers are last-wins within a stanza, matching native: every matching tail
 * is stripped from every line, but only the last number found is displayed.
 */
export function groupDiamondStanzas(lines: LyricLine[]): DiamondChunk[] {
  const out: DiamondChunk[] = [];
  let current: LyricLine[] = [];
  let verseNumber: number | null = null;

  const commit = () => {
    if (current.length === 0) return;
    out.push({ kind: 'stanza', key: current[0].index, lines: current, verseNumber });
    current = [];
    verseNumber = null;
  };

  for (const l of lines) {
    if (l.kind === 'blank' || l.kind === 'section') {
      commit();
      out.push({ kind: 'line', key: l.index, line: l });
      continue;
    }
    if (l.kind !== 'lyric') {
      // Svara lines never carry a verse number (native's diamond pass walks only
      // `contentLine.texts`), so they join the stanza untouched.
      current.push(l);
      continue;
    }
    // classifyLines already lifted a `…N` tail into `number`; the ASCII-dot form
    // survives in the text and is stripped here.
    const { number, cleanedText } = extractVerseNumber(l.text);
    const lineNumber = number ?? l.number ?? null;
    if (lineNumber != null) verseNumber = lineNumber;
    // Clearing `number` keeps the renderer's inline number span out of diamond mode —
    // the number belongs to the stanza gutter now, and must not be shown twice.
    current.push(lineNumber != null ? { ...l, text: cleanedText, number: undefined } : l);
  }
  commit();

  return out;
}
